import { GQLResponsePerimeterXChallengeError } from '@change-corgi/core/gql';
import { translationKey } from '@change-corgi/core/i18n';
import { useI18n } from '@change-corgi/core-react/i18n';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { usePaymentFormPerimeterXCaptcha } from '#src/app/shared/hooks/payments';
import {
	isStripeError,
	setPxDeclineCode,
	stripeErrorI18nKey,
	transformPaymentErrorType,
} from '#src/app/shared/utils/payments';
import type { PaymentType } from '#src/app/shared/utils/payments';

import { usePromoteFormState } from '../../../../../formContext/formState';
import { useTracking } from '../../../useTracking';

type PaymentError = { type?: string; code?: string; message?: string };
// eslint-disable-next-line max-lines-per-function
export function useHandleError(): (
	error: unknown,
	paymentType: PaymentType,
	onTransactionComplete?: (status: 'success' | 'fail') => void,
) => void {
	const [, { clearLoading, setGeneralErrorMessage }] = usePromoteFormState();
	const { trackGeneralError, trackPaymentError } = useTracking();
	const { errorReporter } = useUtilityContext();
	const triggerPerimeterXCaptcha = usePaymentFormPerimeterXCaptcha('corgi_promote');
	const { typedTranslate } = useI18n();
	return function handleError(error, paymentType, onTransactionComplete) {
		setPxDeclineCode(error);
		if (error instanceof GQLResponsePerimeterXChallengeError) {
			trackGeneralError({
				paymentType,
				errorType: 'RequestBlocked',
				errorMessage: 'Human verification required',
			});
			void triggerPerimeterXCaptcha({
				pxResponse: error.pxChallenge,
				operationName: error.operationName,
			});
		} else if (isStripeError(error) && error.type === 'card_error') {
			trackPaymentError({
				paymentType,
				errorMessage: error.message || 'Unknown Error',
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				errorType: transformPaymentErrorType({ error: error as PaymentError | string, paymentType }),
			});
		} else {
			trackGeneralError({
				paymentType,
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				errorMessage: (error as Error).message || 'Unknown Error',
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				errorType: transformPaymentErrorType({ error: error as PaymentError | string, paymentType }),
			});
		}

		clearLoading();
		const errorMessageTranslationKey = isStripeError(error) ? stripeErrorI18nKey(error) : undefined;
		setGeneralErrorMessage(
			typedTranslate(errorMessageTranslationKey ?? translationKey('corgi.co.promote.generic_payment_error')),
		);
		onTransactionComplete && onTransactionComplete('fail');
		void errorReporter.report(
			{
				error: new Error('Failed to submit promotion', { cause: error }),
				params: { paymentType },
			},
			{
				severity: 'warning',
			},
		);
	};
}
